/** @file gizmoNodes: the transform nodes the gizmo meshes hang from (display, axes frame, screen, arc frame). */
import { Quaternion } from '@babylonjs/core/Maths/math.vector.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { basisQuat } from './basisQuat';
import type { GizmoModel } from './gizmoModel';
import { silhouetteOf } from './silhouetteOf';

/**
 * `display` scales Babylon y (world z) by `zScaleView`; under it `frame` and `arcFrame` carry a gizmo's origin,
 * axes and size (children are built in unit coordinates swapped to Babylon's frame); `screen` sits at the display
 * origin facing the camera (children in its x right / y up plane), and `silhouette` under it stretches a unit circle
 * to the rings' outline (`silhouetteOf`). `place` and `placeArc` allocate nothing.
 */
export type GizmoNodes = {
  display: TransformNode; frame: TransformNode; screen: TransformNode; silhouette: TransformNode; arcFrame: TransformNode;
  place(model: GizmoModel, size: number, view: View): void; placeArc(model: GizmoModel, size: number): void; dispose(): void;
};

/** A node with a quaternion rotation (Babylon uses Euler angles until one is set). */
function node(name: string, scene: Scene, parent: TransformNode | null): TransformNode {
  const n = new TransformNode(name, scene);
  n.rotationQuaternion = new Quaternion();
  n.parent = parent;
  return n;
}

/** Origin, axes (X, Z, Y in Babylon's local x, y, z) and uniform size of an axes-frame node. */
function placeAxes(n: TransformNode, model: GizmoModel, size: number): void {
  const [o, [a, b, c]] = [model.origin, model.axes];
  n.position.set(o[0], o[2], o[1]);
  if (n.rotationQuaternion !== null) basisQuat(a, c, b, n.rotationQuaternion);
  n.scaling.setAll(size);
}

/** The four nodes of one gizmo in `scene`. */
export function gizmoNodes(scene: Scene): GizmoNodes {
  const display = node('viewport-gizmo-display', scene, null);
  const frame = node('viewport-gizmo-frame', scene, display);
  const arcFrame = node('viewport-gizmo-arc', scene, display);
  const screen = node('viewport-gizmo-screen', scene, null);
  const silhouette = node('viewport-gizmo-silhouette', scene, screen);
  const ell: [number, number, number] = [1, 1, 0];
  return {
    display, frame, screen, silhouette, arcFrame,
    place: (model, size, view) => {
      const z = view.zScaleView;
      const { right, up, back } = view.basis;
      display.scaling.set(1, z, 1);
      placeAxes(frame, model, size);
      const o = model.origin;
      screen.position.set(o[0], o[2] * z, o[1]);
      if (screen.rotationQuaternion !== null) basisQuat(right, up, back, screen.rotationQuaternion, -1);
      screen.scaling.setAll(size);
      silhouetteOf(view, ell);
      silhouette.scaling.set(ell[0], ell[1], 1);
      silhouette.rotationQuaternion?.set(0, 0, Math.sin(ell[2] / 2), Math.cos(ell[2] / 2));
    },
    placeArc: (model, size) => { placeAxes(arcFrame, model, size); },
    dispose: () => { for (const n of [frame, arcFrame, display, silhouette, screen]) n.dispose(); },
  };
}
